// assets/analytics-data-layer.js

/**
 * @typedef {Object} ShopifyProduct
 * @property {number|string} id
 * @property {string} title
 * @property {string} [vendor]
 * @property {string} [type]
 * @property {number} [price]
 * @property {string} [currency]
 * @property {string} [item_category]
 * @property {string} [parent_collection]
 * @property {string} [parentCollection]
 * @property {string} [imageUrl]
 * @property {string} [featured_image]
 * @property {string|number} [item_variant]
 * @property {string|number} [item_product_id]
 * @property {string} [variant_name]
 * @property {Record<string, any>} [extra]
 */

/**
 * @typedef {Object} ShopifyVariant
 * @property {number|string} id
 * @property {string} [title]
 * @property {string} [sku]
 * @property {string|number} [barcode]
 * @property {number} [price]
 * @property {number} [compare_at_price]
 * @property {Record<string, any>} [extra]
 */

/**
 * @typedef {Object} GA4Item
 * @property {string|number} item_id
 * @property {string} item_name
 * @property {string} [coupon]
 * @property {number} [discount]
 * @property {number} [index]
 * @property {string} [item_brand]
 * @property {string} [item_category]
 * @property {string} [item_list_id]
 * @property {string} [item_list_name]
 * @property {string} [item_variant]
 * @property {string} [location_id]
 * @property {number} [price]
 * @property {number} [compare_at_price]
 * @property {number} [quantity]
 * @property {Record<string, any>} [extraItemData]
 * @property {string} [promotion_id]
 * @property {string} [promotion_name]
 * @property {string} [imageUrl]
 * @property {string|number} [item_product_id]
 * @property {string} [item_variant_name]
 */

/**
 * @typedef {Object} GA4EcommercePayload
 * @property {string} [currency]
 * @property {number} [value]
 * @property {string} [item_list_id]
 * @property {string} [item_list_name]
 * @property {GA4Item[]} items
 */

/**
 * @typedef {Object} MapItemArgs
 * @property {ShopifyProduct} product
 * @property {ShopifyVariant} [variant]
 * @property {number} [quantity]
 * @property {string} [listName]
 * @property {number} [index]
 * @property {string} [affiliation]
 * @property {string} [coupon]
 * @property {number} [discount]
 * @property {string} [locationId]
 * @property {string} [item_category]
 * @property {string} [parent_collection]
 * @property {string} [parentCollection]
 * @property {string} [collection]
 * @property {string} [imageUrl]
 * @property {string} [styleId]
 * @property {Record<string, any>} [extraItemData]
 */

/**
 * @typedef {Object} SingleItemArgs
 * @property {ShopifyProduct} product
 * @property {ShopifyVariant} [variant]
 * @property {number} [quantity]
 * @property {string} [listName]
 * @property {number} [index]
 * @property {string} [affiliation]
 * @property {string} [coupon]
 * @property {string} [item_category]
 * @property {string} [parent_collection]
 * @property {string} [parentCollection]
 * @property {string} [styleId]
 * @property {number} [discount]
 * @property {string} [locationId]
 * @property {string} [currency]
 */

 /**
 * @typedef {SingleItemArgs & {
 *   source?: string
 * }} AddToCartArgs
 */

/**
 * @typedef {SingleItemArgs & {
 *   source?: string
 * }} AddToWishlistArgs
 */

/**
 * @typedef {{
 *   query: string,
 * }} SearchArgs
 */



export class ShopAnalytics {
  static PRODUCT_CONTEXT_STORAGE_KEY = 'ga_product_context';
  static CONSENT_CONTEXT_STORAGE_KEY = 'cql_consent_context';
  static USER_DATA_STORAGE_KEY = 'cql_user_data';

  /**
   * @returns {any[]}
   */
  static get dataLayer() {
    return (window.dataLayer = window.dataLayer || []);
  }

  /**
   * @returns {any}
   */
  static get shopify() {
    return window.Shopify || {};
  }

  /**
   * @returns {Record<string, any>}
   */
  static getCommonContext() {
    const shopify = /** @type {any} */ (this.shopify);
    return {
      currency:
        shopify.currency && shopify.currency.active
          ? shopify.currency.active
          : undefined,
    };
  }

  /**
   * @param {any} payload
   * @returns {void}
   */
  static pushRaw(payload) {
    this.dataLayer.push(payload);
  }


  /**
   * @param {any} value
   * @returns {string | undefined}
   */
  static normalizeConsentCookieValue(value) {
    if (value == null) return undefined;
    const normalizedValue = String(value).trim();
    return normalizedValue || undefined;
  }

  /**
   * @param {string} cookieName
   * @returns {string | undefined}
   */
  static readCookie(cookieName) {
    if (!cookieName) return undefined;

    try {
      const cookiePattern = new RegExp(
        `(?:^|;\\s*)${cookieName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`
      );
      const match = document.cookie.match(cookiePattern);
      return match ? decodeURIComponent(match[1]) : undefined;
    } catch (error) {
      return undefined;
    }
  }

  /**
   * @param {string | undefined} rawValue
   * @returns {Record<string, any> | null}
   */
  static buildConsentContext(rawValue) {
    const normalizedValue = this.normalizeConsentCookieValue(rawValue);
    if (!normalizedValue) return null;

    const grantedBuckets = new Set(normalizedValue.match(/\d+/g) || []);
    const hasFunctionalConsent = grantedBuckets.has('1');
    const hasAnalyticsConsent = grantedBuckets.has('2');
    const hasMarketingConsent = grantedBuckets.has('3');

    return this.cleanObject({
      cmapi_cookie_privacy: normalizedValue,
      functionality_storage: hasFunctionalConsent ? 'granted' : 'denied',
      security_storage: hasFunctionalConsent ? 'granted' : 'denied',
      analytics_storage: hasAnalyticsConsent ? 'granted' : 'denied',
      personalization_storage: hasAnalyticsConsent ? 'granted' : 'denied',
      ad_storage: hasMarketingConsent ? 'granted' : 'denied',
      ad_user_data: hasMarketingConsent ? 'granted' : 'denied',
      ad_personalization: hasMarketingConsent ? 'granted' : 'denied',
      marketing_consent: hasMarketingConsent ? 'granted' : 'denied',
      updated_at: new Date().toISOString(),
    });
  }

  /**
   * @param {string | undefined} rawValue
   * @returns {void}
   */
  static persistConsentContext(rawValue) {
    const context = this.buildConsentContext(rawValue);
    if (!context) return;

    window.__cqlConsentContext = context;
    this.writeStorageObject(
      'sessionStorage',
      this.CONSENT_CONTEXT_STORAGE_KEY,
      context
    );
  }

  /**
   * @returns {void}
   */
  static refreshConsentContextFromCookie() {
    this.persistConsentContext(this.readCookie('cmapi_cookie_privacy'));
  }

  /**
   * @param {any} entry
   * @returns {void}
   */
  static inspectConsentDataLayerEntry(entry) {
    if (!entry || typeof entry !== 'object') return;

    if (
      typeof entry.cmapi_cookie_privacy === 'string' &&
      entry.cmapi_cookie_privacy.trim()
    ) {
      this.persistConsentContext(entry.cmapi_cookie_privacy);
      return;
    }

    if (entry.event === 'cookie_prefs_set') {
      this.refreshConsentContextFromCookie();
    }
  }

  /**
   * Persist consent from the parent cookie so the Shopify Customer Events pixel
   * can reuse normalized consent state inside the sandbox.
   *
   * @returns {void}
   */
  static installConsentBridge() {
    const dataLayer = this.dataLayer;

    this.refreshConsentContextFromCookie();
    dataLayer.forEach((entry) => this.inspectConsentDataLayerEntry(entry));

    if (window.__cqlConsentDataLayerBridgeInstalled) return;

    const originalPush = dataLayer.push;
    const self = this;

    dataLayer.push = function (...entries) {
      entries.forEach((entry) => self.inspectConsentDataLayerEntry(entry));
      return originalPush.apply(this, entries);
    };

    window.__cqlConsentDataLayerBridgeInstalled = true;
  }


  /**
   * Persist user_data from the parent frame so the Shopify Customer Events
   * sandbox can reuse the same identity payload.
   *
   * @param {Record<string, any>} userData
   * @returns {void}
   */
  static persistUserData(userData) {
    if (!userData || typeof userData !== 'object') return;

    let serializedUserData;
    try {
      serializedUserData = JSON.parse(JSON.stringify(userData));
    } catch (error) {
      return;
    }

    const context = this.cleanObject({
      ...serializedUserData,
      updated_at: new Date().toISOString(),
    });

    if (!Object.keys(context).length) return;

    window.__cqlUserData = context;
    this.writeStorageObject(
      'sessionStorage',
      this.USER_DATA_STORAGE_KEY,
      context
    );
  }

  /**
   * @param {any} entry
   * @returns {void}
   */
  static inspectUserDataLayerEntry(entry) {
    if (!entry || typeof entry !== 'object') return;
    if (!entry.user_data || typeof entry.user_data !== 'object') return;

    this.persistUserData(/** @type {Record<string, any>} */ (entry.user_data));
  }

  /**
   * Persist user_data entries from the parent dataLayer so the Shopify
   * Customer Events pixel can decorate sandbox-fired events with the same
   * identity values.
   *
   * @returns {void}
   */
  static installUserDataBridge() {
    const dataLayer = this.dataLayer;

    dataLayer.forEach((entry) => this.inspectUserDataLayerEntry(entry));

    if (window.__cqlUserDataLayerBridgeInstalled) return;

    const originalPush = dataLayer.push;
    const self = this;

    dataLayer.push = function (...entries) {
      entries.forEach((entry) => self.inspectUserDataLayerEntry(entry));
      return originalPush.apply(this, entries);
    };

    window.__cqlUserDataLayerBridgeInstalled = true;
  }

  /**
   * Non-ecommerce event (does NOT clear ecommerce)
   * @param {string} eventName
   * @param {Record<string, any>} [payload={}]
   * @returns {void}
   */
  static pushEvent(eventName, payload = {}) {
    this.pushRaw({
      event: eventName,
      ...payload,
    });
  }

  /**
   * Ecommerce event:
   * 1) Clears ecommerce
   * 2) Pushes GA4 event with ecommerce payload
   *
   * GA4 pattern:
   *   dataLayer.push({ ecommerce: null });
   *   dataLayer.push({ event: 'view_item', ecommerce: { ... } });
   *
   * @param {string} eventName
   * @param {GA4EcommercePayload} ecommercePayload
   * @param {Record<string, any>} [extra={}]
   * @returns {void}
   */
  static pushEcommerceEvent(eventName, ecommercePayload, extra = {}) {
    this.pushRaw({ ecommerce: null });


    this.pushRaw({
      event: eventName,
      ...extra,
      ecommerce: ecommercePayload,
    });
  }

  /**
   * @param {string | undefined} text
   * @returns {string | undefined}
   */
  static safeListId(text) {
    if (!text) return undefined;
    return String(text)
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  }

  /**
   * Remove undefined/null keys but keep the original type shape.
   *
   * @template T
   * @param {T} obj
   * @returns {T}
   */
  static cleanObject(obj) {
    Object.keys(/** @type {Record<string, any>} */ (obj)).forEach((key) => {
      const value = /** @type {Record<string, any>} */ (obj)[key];
      if (value === undefined || value === null) {
        delete /** @type {Record<string, any>} */ (obj)[key];
      }
    });
    return obj;
  }

  /**
   * @param {'localStorage' | 'sessionStorage'} storageName
   * @returns {Storage | undefined}
   */
  static getStorage(storageName) {
    try {
      return window[storageName];
    } catch (error) {
      return undefined;
    }
  }

  /**
   * @param {'localStorage' | 'sessionStorage'} storageName
   * @param {string} key
   * @returns {Record<string, any>}
   */
  static readStorageObject(storageName, key) {
    const storage = this.getStorage(storageName);
    if (!storage) return {};

    try {
      const raw = storage.getItem(key);
      if (!raw) return {};

      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  /**
   * @param {'localStorage' | 'sessionStorage'} storageName
   * @param {string} key
   * @param {Record<string, any>} value
   * @returns {void}
   */
  static writeStorageObject(storageName, key, value) {
    const storage = this.getStorage(storageName);
    if (!storage) return;

    try {
      storage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Storage can fail in privacy-restricted contexts.
    }
  }

  /**
   * Persist category context so the pixel sandbox can reuse it later.
   *
   * @param {{
   *   item: GA4Item,
   *   product?: ShopifyProduct,
   *   variant?: ShopifyVariant
   * }} args
   * @returns {void}
   */
  static persistProductContext(args) {
    const item = args?.item;
    const product = args?.product;
    const variant = args?.variant;
    const itemCategory =
      typeof item?.item_category === 'string' ? item.item_category.trim() : '';

    if (!itemCategory) return;

    const nextEntry = this.cleanObject({
      item_category: itemCategory,
      item_name: item?.item_name,
      item_id: item?.item_id,
      item_variant: item?.item_variant,
      item_variant_name: item?.item_variant_name,
      item_product_id: item?.item_product_id,
      item_list_id: item?.item_list_id,
      item_list_name: item?.item_list_name,
      imageUrl: item?.imageUrl,
      compare_at_price: item?.compare_at_price,
      price: item?.price,
      product_id: product?.id != null ? String(product.id) : undefined,
      variant_id: variant?.id != null ? String(variant.id) : undefined,
      product_url: product?.url,
      updated_at: new Date().toISOString(),
    });

    const storageValue = this.readStorageObject(
      'sessionStorage',
      this.PRODUCT_CONTEXT_STORAGE_KEY
    );

    const byItemId = storageValue.byItemId && typeof storageValue.byItemId === 'object'
      ? storageValue.byItemId
      : {};
    const byProductId = storageValue.byProductId && typeof storageValue.byProductId === 'object'
      ? storageValue.byProductId
      : {};
    const byVariantId = storageValue.byVariantId && typeof storageValue.byVariantId === 'object'
      ? storageValue.byVariantId
      : {};
    const byProductUrl = storageValue.byProductUrl && typeof storageValue.byProductUrl === 'object'
      ? storageValue.byProductUrl
      : {};

    if (nextEntry.item_id) {
      byItemId[String(nextEntry.item_id)] = nextEntry;
    }

    if (nextEntry.product_id) {
      byProductId[String(nextEntry.product_id)] = nextEntry;
    }

    if (nextEntry.variant_id) {
      byVariantId[String(nextEntry.variant_id)] = nextEntry;
    }

    if (nextEntry.product_url) {
      byProductUrl[String(nextEntry.product_url)] = nextEntry;
    }

    this.writeStorageObject('sessionStorage', this.PRODUCT_CONTEXT_STORAGE_KEY, {
      lastUpdated: nextEntry.updated_at,
      lastItem: nextEntry,
      byItemId,
      byProductId,
      byVariantId,
      byProductUrl,
    });
  }

    /**
   * USER DATA (plain + SHA-256 hashed)
   *
   * Expected raw shape (usually set by Liquid):
   * window.__userRaw = {
   *   userFirstName, userLastName, userEmail, userPhone
   * }
   */

  /**
   * @param {any} v
   * @returns {string | undefined}
   */
  static _cleanString(v) {
    if (typeof v !== 'string') return undefined;
    const t = v.trim();
    return t ? t : undefined;
  }

  /**
   * @param {any} v
   * @returns {string | undefined}
   */
  static _cleanCategoryValue(v) {
    if (v == null) return undefined;

    if (typeof v === 'object') {
      return (
        this._cleanCategoryValue(v.value) ||
        this._cleanCategoryValue(v.title) ||
        this._cleanCategoryValue(v.name) ||
        this._cleanCategoryValue(v.fullName) ||
        this._cleanCategoryValue(v.label)
      );
    }

    const t = String(v).trim();
    return t ? t : undefined;
  }

  /**
   * Resolve GA4 item_category from explicit parent collection values only.
   *
   * @param {Record<string, any>} args
   * @returns {string | undefined}
   */
  static resolveItemCategory(args = {}) {
    return (
      this._cleanCategoryValue(args.item_category) ||
      this._cleanCategoryValue(args.parentCollection) ||
      this._cleanCategoryValue(args.parent_collection)
    );
  }

  /**
   * @param {string | undefined} v
   * @returns {string | undefined}
   */
  static _normalizeEmail(v) {
    const s = this._cleanString(v);
    return s ? s.toLowerCase() : undefined;
  }

  /**
   * @param {string | undefined} v
   * @returns {string | undefined}
   */
  static _normalizePhone(v) {
    const s = this._cleanString(v);
    return s ? s.replace(/\s+/g, '') : undefined;
  }

  /**
   * @param {string | undefined} input
   * @returns {Promise<string | undefined>}
   */
  static async _sha256(input) {
    if (!input || !window.crypto?.subtle) return undefined;

    const encoder = new TextEncoder();
    const buf = await crypto.subtle.digest('SHA-256', encoder.encode(input));
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Build the final user payload (plain + hashed) and remove empty keys
   * @param {Record<string, any>} raw
   * @returns {Promise<Record<string, any>>}
   */
  static async buildUserObject(raw = {}) {
    const first = this._cleanString(raw.userFirstName);
    const last = this._cleanString(raw.userLastName);
    const email = this._normalizeEmail(raw.userEmail);
    const phone = this._normalizePhone(raw.userPhone);
    const user_id = raw.user_id;
    const page_type = raw.page_type;
    const user_type = raw.userType;

    /** @type {Record<string, any>} */
    const user = {};

    if (user_id) user.user_id = user_id;
    if (first) user.userFirstName = first;
    if (last) user.userLastName = last;
    if (email) user.userEmail = email;
    if (email) user.email_address = email;
    if (phone) user.userPhone = phone;
    if (phone) user.phone_number = phone;
    if (page_type) user.page_type = page_type;
    if (user_type) user.user_type = user_type;

    const [hashedFirstName, hashedLastName, hashedEmail, hashedPhone] =
      await Promise.all([
        this._sha256(first),
        this._sha256(last),
        this._sha256(email),
        this._sha256(phone),
      ]);

    if (hashedFirstName) user.hashedFirstName = hashedFirstName;
    if (hashedLastName) user.hashedLastName = hashedLastName;
    if (hashedEmail) user.hashedEmail = hashedEmail;
    if (hashedEmail) user.hashedEmailAddress = hashedEmail;
    if (hashedPhone) user.hashedPhone = hashedPhone;
    if (hashedPhone) user.hashedPhoneNumber = hashedPhone;

    return this.cleanObject(user);
  }

  /**
   * Push user object into dataLayer (no ecommerce clear)
   * @param {Record<string, any>} raw
   * @returns {Promise<void>}
   */
  static async pushUserData(raw = {}) {
    const user_data = await this.buildUserObject(raw);
    if (!user_data|| !Object.keys(user_data).length) return;

    this.persistUserData(user_data);

    // Requirement: push "user" object on all pages
    this.pushRaw({ user_data});

    // Optional (uncomment if you want an explicit event too)
    // this.pushEvent('user_data', { user });
  }

  /**
   * Auto-init from window.__userRaw (if present)
   * @returns {void}
   */
  static initUserData() {
    const raw = /** @type {any} */ (window.__userRaw);
    if (!raw) return;

    // fire and forget (avoid blocking page)
    this.pushUserData(raw).catch(() => {});
  }

  /**
   * Normalize Shopify product + variant into a GA4 item
   *
   * args:
   *  - product, variant
   *  - quantity, listName, index, affiliation, coupon, discount, locationId, item_category
   *  - extraItemData (for custom props if needed)
   *
   * @param {MapItemArgs} args
   * @returns {GA4Item}
   */
  static mapProductVariantToItem(args) {
    const {
      item_category,
      parent_collection,
      parentCollection,
      styleId,
      product,
      variant,
      quantity,
      listName,
      index,
      coupon,
      locationId,
      extraItemData = {},
    } = args;
    const resolvedItemCategory = this.resolveItemCategory({
      item_category,
      parentCollection,
      parent_collection,
    });

    const priceCents =
      (variant && typeof variant.price === 'number' && variant.price) ??
      (typeof product.price === 'number' ? product.price : 0);

    const compareCents =  (variant && typeof variant.compare_at_price === 'number' && variant.compare_at_price) ??
      (typeof product.compare_at_price === 'number' ? product.compare_at_price : 0);

    const price = priceCents / 100;
    const comparePrice = compareCents / 100;

    let discountPrice;

    if (comparePrice > 0 && price > 0 && comparePrice > price) {
      discountPrice = comparePrice - price;
    }

    const variant_id = product?.item_variant?.toString() || variant?.id?.toString() || product?.variant_id;

    /** @type {GA4Item} */
    const item = {
      item_id: styleId || product?.styleId,
      item_name: product.title,
      coupon,
      discount: discountPrice,
      index,
      compare_at_price: comparePrice ? comparePrice : undefined,
      item_brand: product.vendor,
      ...(resolvedItemCategory ? { item_category: resolvedItemCategory } : {}),
      imageUrl: product?.imageUrl || product?.featured_image,
      item_list_id: listName ? this.safeListId(listName) : undefined,
      item_list_name: listName ? listName : undefined,
      item_variant: variant_id,
      // From BR barcode comes with product data see: SearchResultsViewItemListAnalytics, onClickCapture
      item_product_id: variant?.barcode?.toString() || product.barcode?.toString() || '',
      item_variant_name: product?.variant_name || variant?.title,
      location_id: locationId,
      price,
      quantity,
      ...extraItemData,
    };

    return /** @type {GA4Item} */ (this.cleanObject(item));
  }

  /**
   * @param {SingleItemArgs} args
   * @param {{ listName?: string, index?: number }} [defaults={}]
   * @returns {GA4EcommercePayload}
   */
  static buildSingleItemEcommercePayload(args, defaults = {}) {
    const {
      item_category,
      parent_collection,
      parentCollection,
      product,
      variant,
      quantity,
      listName = defaults.listName,
      index = defaults.index ?? 0,
      coupon,
      discount,
      locationId,
      styleId,
      currency: currencyOverride,
    } = args;

    const item = this.mapProductVariantToItem({
      item_category,
      parent_collection,
      parentCollection,
      product,
      variant,
      quantity,
      listName,
      index,
      coupon,
      discount,
      locationId,
      styleId
    });

    this.persistProductContext({
      item,
      product,
      variant,
    });

    const currency = this.resolveCurrency(
      currencyOverride ?? (product && product.currency)
    );

    const value = (item.price || 0) * (item.quantity || 1);

    return {
      currency,
      value,
      items: [item],
    };
  }

  /**
   * @param {string | undefined} [fallbackCurrency]
   * @returns {string}
   */
  static resolveCurrency(fallbackCurrency) {
    const shopify = /** @type {any} */ (this.shopify);
    return (
      (shopify.currency && shopify.currency.active) ||
      fallbackCurrency ||
      'USD'
    );
  }

  /**
   * view_item
   *
   * @param {SingleItemArgs} args
   * @returns {void}
   */
  static viewItem(args) {
    const ecommercePayload = this.buildSingleItemEcommercePayload(args, {
      listName: 'PDP',
      index: 0,
    });

    this.pushEcommerceEvent('view_item', ecommercePayload);
  }

  /**
   * Persist PDP product context for downstream consumers without emitting
   * a page-context view_item event.
   *
   * @param {SingleItemArgs} args
   * @returns {void}
   */
  static persistProductContextOnly(args) {
    this.buildSingleItemEcommercePayload(args, {
      listName: 'PDP',
      index: 0,
    });
  }

  /**
   * view_item_list
   *
   * args:
   *  - listName
   *  - items: [{ product, variant, quantity?, index?, coupon?, discount?, locationId?, extraItemData? }]
   *  - currency (optional override)
   */
  /**
   * @typedef {Object} ViewItemListItem
   * @property {ShopifyProduct} product
   * @property {ShopifyVariant} [variant]
   * @property {number} [index]
   * @property {Record<string, any>} [extraItemData]
   */

  /**
   * @typedef {Object} ViewItemListArgs
   * @property {string} [listName]
   * @property {ViewItemListItem[]} [items]
   * @property {string} [currency]
   *
   * @param {ViewItemListArgs} args
   * @returns {void}
   */
  static viewItemList(args) {
    const {
      listName = 'Related products',
      items = [],
      // `currency` not required for GA4 view_item_list, so we ignore it by default
    } = args;

    const listId = this.safeListId(listName);

    /** @type {GA4Item[]} */
    const mappedItems = items.map((entry, idx) =>
      this.mapProductVariantToItem({
        listName,
        index: entry.index != null ? entry.index : idx,
        ...entry,
      })
    );

    /** @type {GA4EcommercePayload} */
    const ecommercePayload = this.cleanObject({
      ...this.getCommonContext(),
      item_list_id: listId,
      item_list_name: listName,
      items: mappedItems,
    });

    this.pushEcommerceEvent('view_item_list', ecommercePayload);
  }

  /**
   * Map /cart.js line item to GA4 item for view_cart
   *
   * @param {any} line
   * @param {number} index
   * @returns {GA4Item}
   */
  static mapCartLineToItem(line, index) {
    const qty =
      typeof line.quantity === 'number' && line.quantity > 0
        ? line.quantity
        : 1;

    const finalLinePriceCents =
      typeof line.final_line_price === 'number'
        ? line.final_line_price
        : typeof line.line_price === 'number'
        ? line.line_price
        : 0;

    const originalLinePriceCents =
      typeof line.original_line_price === 'number'
        ? line.original_line_price
        : finalLinePriceCents;

    const unitPrice =
      qty > 0 ? finalLinePriceCents / qty / 100 : 0;

    const discountTotalCents =
      originalLinePriceCents > finalLinePriceCents
        ? originalLinePriceCents - finalLinePriceCents
        : 0;

    const discount =
      discountTotalCents > 0 ? discountTotalCents / 100 : undefined;


    const variant_id = line.variant_id?.toString();
    const barcode = line.properties?._barcode;
    const itemCategory = this.resolveItemCategory({
      parentCollection: line.properties?._parentCollection,
      parent_collection: line.properties?._parent_collection,
    });

    /** @type {GA4Item} */
    const item = {
      item_id: line.properties?.styleId,
      item_name: line.product_title || line.title,
      item_brand: line.vendor,
      ...(itemCategory ? { item_category: itemCategory } : {}),
      image: line.image,
      item_variant_name: line.variant_title,
      item_variant: variant_id,
      item_product_id: barcode,
      price: unitPrice,
      quantity: qty,
      index,
      discount,
    };

    return /** @type {GA4Item} */ (this.cleanObject(item));
  }

  /**
   * Build GA4 ecommerce payload for view_cart from /cart.js payload
   *
   * @param {{ items: any[], currency?: string }} cart
   * @returns {GA4EcommercePayload}
   */
  static buildViewCartEcommercePayload(cart) {
    const itemsArray = Array.isArray(cart.items) ? cart.items : [];

    /** @type {GA4Item[]} */
    const items = itemsArray.map((line, index) =>
      this.mapCartLineToItem(line, index)
    );

    const currency =
      (typeof cart.currency === 'string' && cart.currency) ||
      this.resolveCurrency();

    const value = items.reduce((sum, item) => {
      const price = item.price || 0;
      const qty = item.quantity || 1;
      return sum + price * qty;
    }, 0);

    return /** @type {GA4EcommercePayload} */ (
      this.cleanObject({
        currency,
        value,
        items,
      })
    );
  }

  /**
   * view_cart
   *
   * @param {{ cart: any, source?: string }} args
   * @returns {void}
   */
  static viewCart(args) {
    const cart = args && args.cart;
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) return;

    const ecommercePayload = this.buildViewCartEcommercePayload(cart);

    const source = args && args.source;

    this.pushEcommerceEvent('view_cart', ecommercePayload, {
      cart_source: source,
    });
  }

 /**
   * add_to_cart
   *
   * @param {AddToCartArgs} args
   * @returns {void}
   */
  static addToCart(args) {
    // allow a custom source (PDP, collection, upsell, etc.)
    const { source, ...itemArgs } = args || {};


    // Reuse your existing builder for item + value + currency
    const ecommercePayload = this.buildSingleItemEcommercePayload(
      /** @type {SingleItemArgs} */ (itemArgs),
      {}
    );

    /** @type {Record<string, any>} */
    const extra = {};
    if (source) {
      extra.add_to_cart_source = source; // helpful custom dimension
    }

    this.pushEcommerceEvent('add_to_cart', ecommercePayload, extra);
  }

  /**
   * add_to_wishlist
   *
   * @param {AddToWishlistArgs} args
   * @returns {void}
   */
  static addToWishlist(args) {
    const { source, ...itemArgs } = args || {};

    const ecommercePayload = this.buildSingleItemEcommercePayload(
      /** @type {SingleItemArgs} */ (itemArgs),
      {}
    );

    /** @type {Record<string, any>} */
    const extra = {};
    if (source) {
      extra.add_to_wishlist_source = source;
    }

    this.pushEcommerceEvent('add_to_wishlist', ecommercePayload, extra);
  }

  /**
   * remove_from_cart
   *
   * @param {RemoveFromCartArgs} args
   */
  static removeFromCart(args) {
    const { source, ...itemArgs } = args ?? {};

    const ecommercePayload = this.buildSingleItemEcommercePayload(
      itemArgs,
      {}
    );
    const extra = {};
    if (source) {
      extra.remove_from_cart_source = source; 
    }

    this.pushEcommerceEvent('remove_from_cart', ecommercePayload, extra);
  }


   /**
   * select_item
   *
   * @param {SingleItemArgs} args
   */
  static selectItem(args) {
    const ecommercePayload = this.buildSingleItemEcommercePayload(args, {
      listName: 'PDP',
      index: 0,
    });
    this.pushEcommerceEvent('select_item', ecommercePayload);
  }

  /**
   * select_item
   *
   * @param {SearchArgs} args
   */
  static search(args) {
    const searchTerm = (args?.query ?? '').toString().trim();
    if (!searchTerm) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'search',
      ecommerce: {
        search_term: searchTerm,
      }
    });
  }

  /**
   * Track customer login once per session using localStorage
   */
  static trackCustomerLogin() {
    const STORAGE_KEY = 'ga_customer_logged_in_sent';

    const hasCustomer = window.__userRaw?.user_id;

    const alreadySent = localStorage.getItem(STORAGE_KEY) === 'true';

    if (hasCustomer && !alreadySent) {
      const raw = /** @type {any} */ (window.__userRaw || {});

      this.pushEvent('user_login', {
        user_id: raw.user_id,
        user_email: raw.userEmail
      });

      localStorage.setItem(STORAGE_KEY, 'true');
      return;
    }

    if (!hasCustomer) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

}

/** @type {{
 *  viewItem: (args: SingleItemArgs) => void;
 *  viewItemList: (args: ViewItemListArgs) => void;
 *  viewCart: (args: { cart: any, source?: string }) => void;
 *  addToCart: (args: AddToCartArgs) => void;
 *  addToWishlist: (args: AddToWishlistArgs) => void;
 *  removeFromCart: (args: RemoveFromCartArgs) => void;
 *  selectItem: (args: SingleItemArgs) => void;
 *  search: (args: SearchArgs) => void;
 * }} */
ShopAnalytics.events = {
  viewItem: (args) => ShopAnalytics.viewItem(args),
  persistProductContextOnly: (args) => ShopAnalytics.persistProductContextOnly(args),
  viewItemList: (args) => ShopAnalytics.viewItemList(args),
  viewCart: (args) => ShopAnalytics.viewCart(args),
  addToCart: (args) => ShopAnalytics.addToCart(args),
  addToWishlist: (args) => ShopAnalytics.addToWishlist(args),
  removeFromCart: (args) => ShopAnalytics.removeFromCart(args),
  selectItem: (args) => ShopAnalytics.selectItem(args),
  search: (args) => ShopAnalytics.search(args),
};

// Attach to window (typed via global.d.ts)
window.ShopAnalytics = ShopAnalytics;

window.ShopAnalytics.installConsentBridge();
window.ShopAnalytics.installUserDataBridge();

// Auto-push user data if Liquid populated window.__userRaw
window.ShopAnalytics.initUserData();
window.ShopAnalytics.trackCustomerLogin();
